// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import * as api from '../../api/client'
import { relativeTime } from '../../components/workflow/StatePills'
import SearchableSelect from '../../components/common/SearchableSelect'

const TYPE_LABELS: Record<string, string> = {
  rx: 'Prescription / Rx', pa: 'Prior Authorization', appeal: 'Appeal / PA Reconsideration',
  smn: 'Statement of Medical Necessity', nursing_notes: 'Nursing Notes',
  pharmacist_assessment: 'Pharmacist Assessment', lab_results: 'Lab Results',
  chart_notes: 'Chart / Progress Notes', benefits_verification: 'Benefits Verification',
  copay_pap: 'Copay / PAP Application', rems: 'REMS Enrollment / Attestation',
  consent_hipaa: 'Patient Consent / HIPAA Auth', enrollment_hub: 'Enrollment / Hub Referral',
  refill: 'Refill Request', delivery_confirmation: 'Delivery / Shipment Confirmation',
  discontinuation_transfer: 'Discontinuation / Transfer',
}
const LIFE_LABELS: Record<string, string> = {
  pending_receipt: 'Pending receipt', received: 'Received', in_review: 'In review',
  action_required: 'Action required', on_hold: 'On hold', completed: 'Completed', cancelled: 'Cancelled',
}
const OUTCOME_LABELS: Record<string, string> = {
  na: 'N/A', submitted: 'Submitted', approved: 'Approved', denied: 'Denied', appealed: 'Appealed', expired: 'Expired',
}
const SIG_LABELS: Record<string, string> = {
  not_required: 'Not required', draft: 'Draft', pending_internal: 'Pending sig · internal',
  pending_prescriber: 'Pending sig · prescriber', pending_patient: 'Pending sig · patient',
  sent: 'Sent for signature', partially_signed: 'Partially signed', signed: 'Signed',
  declined: 'Declined', voided: 'Expired / Voided',
}

const C = {
  grey: ['#EEEBE5', '#847E73'], amber: ['#FBEFD6', '#946317'], green: ['#E5F2E9', '#1E7A4D'],
  red: ['#FBE6E1', '#B23A22'], blue: ['#E5EDF7', '#2C5C97'], purple: ['#EEE8F7', '#6B4AA0'],
  faint: ['transparent', '#A9A296'],
}
const lifeColor = (s: string) => ({ pending_receipt: C.grey, received: C.blue, in_review: C.amber, action_required: C.red, on_hold: C.amber, completed: C.green, cancelled: C.grey }[s] || C.grey)
const outColor = (s: string) => ({ na: C.faint, submitted: C.blue, approved: C.green, denied: C.red, appealed: C.purple, expired: C.grey }[s] || C.grey)
const sigColor = (s: string) => (s === 'not_required' ? C.faint : s === 'signed' ? C.green : (s === 'declined' || s === 'voided') ? C.red : s === 'sent' ? C.blue : C.amber)

function Pill({ lab, text, c, dashed }: { lab: string; text: string; c: string[]; dashed?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600,
      borderRadius: 999, padding: '3px 9px', background: c[0], color: c[1],
      border: dashed ? '1px dashed #DED8CC' : '1px solid transparent', whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 9.5, fontWeight: 700, opacity: .6, textTransform: 'uppercase', letterSpacing: .4 }}>{lab}</span>{text}
    </span>
  )
}

export default function PatientDocuments({ patientId }: { patientId: string }) {
  const [list, setList] = useState<api.PatientDocumentList | null>(null)
  const [hidden, setHidden] = useState(false)
  const [adding, setAdding] = useState(false)
  const [newType, setNewType] = useState('lab_results')
  const [newTitle, setNewTitle] = useState('')
  const [newAudience, setNewAudience] = useState<string[]>(['internal'])
  const [newFile, setNewFile] = useState<File | null>(null)
  const [newExpiration, setNewExpiration] = useState('')
  const [newPrescriberId, setNewPrescriberId] = useState('')
  const [prescribers, setPrescribers] = useState<api.PatientPrescriber[]>([])
  const [dlId, setDlId] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [formErr, setFormErr] = useState<string | null>(null)
  const [confirmId, setConfirmId] = useState<string | null>(null)
  const [delId, setDelId] = useState<string | null>(null)

  const load = () => api.getPatientDocuments(patientId).then(setList).catch(() => setHidden(true))
  useEffect(() => { setHidden(false); load() }, [patientId])

  const shareToProvider = newAudience.includes('prescriber') || newAudience.includes('nurse')
  useEffect(() => {
    if (adding && shareToProvider && prescribers.length === 0)
      api.getPatientRxPrescribers(patientId).then(r => setPrescribers(r.items || [])).catch(() => {})
  }, [adding, shareToProvider, prescribers.length, patientId])

  async function del(docId: string) {
    setDelId(docId)
    try { await api.deletePatientDocument(patientId, docId); setConfirmId(null); await load() }
    catch { /* leave row; surfaced as no-op */ }
    finally { setDelId(null) }
  }

  if (hidden) return null
  if (!list) return null

  const canWrite = list.can_write
  const docs = list.documents

  async function add() {
    setBusy(true); setFormErr(null)
    try {
      if (newFile) {
        const form = new FormData()
        form.append('file', newFile)
        form.append('document_type', newType)
        if (newTitle.trim()) form.append('title', newTitle.trim())
        if (newExpiration) form.append('expiration_date', newExpiration)
        form.append('audience', JSON.stringify(newAudience))
        if (newPrescriberId && shareToProvider) form.append('prescriber_id', newPrescriberId)
        await api.uploadPatientDocument(patientId, form)
      } else {
        await api.createPatientDocument(patientId, {
          document_type: newType, title: newTitle.trim() || undefined,
          audience: newAudience, expiration_date: newExpiration || undefined,
        })
      }
      setAdding(false); setNewTitle(''); setNewFile(null); setNewExpiration(''); setNewAudience(['internal']); setNewPrescriberId('')
      await load()
    } catch (e: any) {
      setFormErr(e?.message || 'Could not add the document.')
    } finally { setBusy(false) }
  }

  async function download(docId: string) {
    setDlId(docId)
    try {
      const r = await api.downloadPatientDocument(patientId, docId)
      window.open(r.url, '_blank', 'noopener')
    } catch (e: any) {
      setFormErr(e?.message || 'Could not open the file.')
    } finally { setDlId(null) }
  }

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 22, marginBottom: 10 }}>
        <h2 style={{ fontSize: 17, margin: 0, letterSpacing: '-.2px' }}>Documents ({docs.length})</h2>
        <div style={{ flex: 1 }} />
        {canWrite && !adding && (
          <button onClick={() => setAdding(true)} style={btnPrimary}>+ Add document</button>
        )}
      </div>

      <div style={cardStyle}>
        {adding && (
          <div style={{ borderBottom: '1px solid var(--color-border,#E8E3DA)', paddingBottom: 12, marginBottom: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <label style={fieldL}><span style={fieldLk}>Document type</span>
                <div style={{ marginTop: 2 }}>
                  <SearchableSelect
                    value={newType}
                    onChange={setNewType}
                    ariaLabel="Document type"
                    options={Object.entries(TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
                  />
                </div></label>
              <label style={fieldL}><span style={fieldLk}>Title (optional)</span>
                <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g. CMP / CBC — monitoring" style={inputStyle} /></label>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
              <label style={fieldL}><span style={fieldLk}>Attachment (optional)</span>
                <input type="file" accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.gif,.doc,.docx" onChange={e => setNewFile(e.target.files?.[0] ?? null)} style={{ fontSize: 12.5 }} /></label>
              <label style={fieldL}><span style={fieldLk}>Expiration date (optional)</span>
                <input type="date" value={newExpiration} onChange={e => setNewExpiration(e.target.value)} style={inputStyle} /></label>
            </div>
            <div style={{ marginTop: 10 }}>
              <span style={fieldLk}>Who can see this document?</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                {AUDIENCE_OPTIONS.map(opt => {
                  const on = newAudience.includes(opt.key)
                  return (
                    <label key={opt.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5,
                      padding: '5px 9px', borderRadius: 6, cursor: 'pointer',
                      border: '1px solid ' + (on ? 'var(--color-primary,#2563eb)' : 'var(--color-border,#E8E3DA)'),
                      background: on ? 'var(--color-primary-soft,#eff6ff)' : 'transparent' }}>
                      <input type="checkbox" checked={on} onChange={e => {
                        setNewAudience(prev => e.target.checked ? Array.from(new Set([...prev, opt.key])) : prev.filter(k => k !== opt.key))
                      }} />
                      {opt.label}
                    </label>
                  )
                })}
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--color-text-mute,#7C766B)', marginTop: 5 }}>
                Internal-only by default. External audiences only see documents explicitly shared to them.
              </div>
            </div>
            {shareToProvider && (
              <div style={{ marginTop: 10 }}>
                <span style={fieldLk}>Notify which provider?</span>
                <div style={{ marginTop: 6 }}>
                  <SearchableSelect
                    value={newPrescriberId}
                    onChange={setNewPrescriberId}
                    ariaLabel="Notify which provider"
                    placeholder={prescribers.length ? 'Search this patient’s providers…' : 'No providers on this patient’s Rx'}
                    emptyText="No matching provider"
                    options={[
                      { value: '', label: 'No specific provider', hint: 'visible to all providers, no email' },
                      ...prescribers.map(p => ({
                        value: p.id,
                        label: p.display_name + (p.clinic_name ? ` · ${p.clinic_name}` : ''),
                        hint: p.has_account ? 'will be notified' : 'no portal account — won’t be notified',
                      })),
                    ]}
                  />
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--color-text-mute,#7C766B)', marginTop: 5 }}>
                  Only this patient’s prescribing providers are listed. Picking one shares the document with them and emails them — if they have a portal account. Leave blank to share to all providers without an email.
                </div>
              </div>
            )}
            {formErr && <div style={{ color: '#B23A22', fontSize: 12, marginTop: 6 }}>{formErr}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={add} disabled={busy} style={btnPrimary}>{busy ? 'Saving…' : 'Create document'}</button>
              <button onClick={() => { setAdding(false); setFormErr(null) }} style={btnGhost}>Cancel</button>
            </div>
          </div>
        )}

        {docs.length === 0 ? (
          <div style={{ color: 'var(--color-text-mute,#7C766B)', fontSize: 13, padding: '4px 0' }}>
            No documents yet.{canWrite ? ' Use “Add document” to start tracking one.' : ''}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            {docs.map(d => (
              <div key={d.id} style={rowStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
                  <div style={{ fontWeight: 700, fontSize: 14, display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>{TYPE_LABELS[d.document_type] || d.document_type}{(d.audience ?? ['internal']).map(a => <span key={a} style={audienceBadge}>{AUDIENCE_LABEL[a] || a}</span>)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 11.5, color: 'var(--color-text-mute,#7C766B)' }}>
                      {d.title ? d.title + ' · ' : ''}{relativeTime(d.created_at)}{d.expiration_date ? ' · expires ' + d.expiration_date : ''}
                    </span>
                    {d.has_file && <button onClick={() => download(d.id)} disabled={dlId === d.id} style={linkBtn} aria-label="Download file">{dlId === d.id ? '…' : 'Download'}</button>}
                    {canWrite && (confirmId === d.id ? (
                      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 11.5, color: '#B23A22' }}>Delete?</span>
                        <button onClick={() => del(d.id)} disabled={delId === d.id} style={delConfirmBtn}>{delId === d.id ? '…' : 'Yes'}</button>
                        <button onClick={() => setConfirmId(null)} style={linkBtn}>No</button>
                      </span>
                    ) : (
                      <button onClick={() => setConfirmId(d.id)} style={linkBtn} aria-label="Delete document">Delete</button>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 9 }}>
                  <Pill lab="lifecycle" text={LIFE_LABELS[d.lifecycle_status] || d.lifecycle_status} c={lifeColor(d.lifecycle_status)} />
                  <Pill lab="outcome" text={OUTCOME_LABELS[d.outcome] || d.outcome} c={outColor(d.outcome)} dashed={d.outcome === 'na'} />
                  <Pill lab="signature" text={SIG_LABELS[d.signature_status] || d.signature_status} c={sigColor(d.signature_status)} dashed={d.signature_status === 'not_required'} />
                  {d.countersignature_required && <Pill lab="" text="⚑ countersign" c={C.purple} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}

const cardStyle: React.CSSProperties = { background: 'var(--color-surface,#fff)', border: '1px solid var(--color-border,#E8E3DA)', borderRadius: 11, padding: 14 }
const AUDIENCE_OPTIONS: { key: string; label: string }[] = [
  { key: 'internal', label: 'Internal only' },
  { key: 'nurse', label: 'Nurse' },
  { key: 'prescriber', label: 'Prescriber' },
  { key: 'caregiver', label: 'Caregiver' },
  { key: 'patient', label: 'Patient' },
  { key: 'external', label: 'External' },
]
const AUDIENCE_LABEL: Record<string, string> = Object.fromEntries(AUDIENCE_OPTIONS.map(o => [o.key, o.label]))
const audienceBadge: React.CSSProperties = {
  fontSize: 10.5, fontWeight: 600, padding: '1px 7px', borderRadius: 999,
  background: 'var(--color-primary-soft,#eef2ff)', color: 'var(--color-primary,#3949ab)',
  border: '1px solid var(--color-border,#E8E3DA)',
}
const rowStyle: React.CSSProperties = { background: 'var(--color-bg,#FBFAF8)', border: '1px solid var(--color-border,#E8E3DA)', borderRadius: 10, padding: '11px 13px' }
const btnPrimary: React.CSSProperties = { background: '#D7A75C', border: '1px solid #C9963F', color: '#3a2a08', borderRadius: 9, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const btnGhost: React.CSSProperties = { background: 'var(--color-surface,#fff)', border: '1px solid #DED8CC', color: 'var(--color-text,#1C1B19)', borderRadius: 9, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const inputStyle: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid #DED8CC', borderRadius: 9, background: 'var(--color-surface,#fff)', fontSize: 13.5, color: 'var(--color-text,#1C1B19)' }
const fieldL: React.CSSProperties = { display: 'block' }
const fieldLk: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute,#7C766B)', display: 'block', marginBottom: 4 }
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-text-mute,#7C766B)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', padding: 0, textDecoration: 'underline' }
const delConfirmBtn: React.CSSProperties = { background: '#FBE6E1', border: '1px solid #ECC4BA', color: '#B23A22', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', borderRadius: 6, padding: '2px 8px' }
